import React, { memo, useCallback } from 'react';
import { DashboardFolderT, DashboardT } from 'business/definitions';
import DashboardCard from 'ui/Card/DashboardCard';
import { Box, useTheme } from '@mui/system';
import DashboardFolderCard from 'ui/Card/DashboardFolderCard';
import { Breadcrumbs, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

type Props = {
    dashboards: DashboardT[];
    isSearching?: boolean;
    folders?: DashboardFolderT[];
    currentFolder?: DashboardFolderT | null;
    handleFolder?: (folder: DashboardFolderT | null) => void;
};

const DashboardsGrid = ({
    dashboards,
    isSearching = false,
    currentFolder = null,
    folders = [],
    handleFolder = undefined,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const theme = useTheme();

    const renderItem = useCallback(
        (dashboard: DashboardT) => (
            <Box
                data-testid={`dashboard-item ${dashboard.title}`}
                key={dashboard.slug}
            >
                <DashboardCard
                    allowMove={true}
                    dashboard={dashboard}
                    size="md"
                    shouldShowFolder={isSearching}
                />
            </Box>
        ),
        [isSearching],
    );

    const renderFolder = useCallback(
        (folder: DashboardFolderT) => (
            <Box
                data-testid={`dashboard-folder-item ${folder.name}`}
                key={`${folder.name} - ${folder.id}`}
            >
                <DashboardFolderCard
                    folder={folder}
                    onClick={() => {
                        if (handleFolder) handleFolder(folder);
                    }}
                />
            </Box>
        ),
        [handleFolder],
    );

    const content = (
        <Box
            className="container"
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, 375px)',
                justifyContent: 'space-evenly',
                gap: 2,
            }}
            data-testid="dashboards-grid"
        >
            {!currentFolder && folders.map((folder) => renderFolder(folder))}
            {dashboards.map((dashboard) => renderItem(dashboard))}
        </Box>
    );

    const tabUrlParam = new URLSearchParams(window.location.search).get('tab');

    return (
        <Box
            sx={{
                margin: theme.spacing(2),
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(2),
            }}
        >
            {currentFolder && handleFolder && (
                <Breadcrumbs
                    separator={<NavigateNextIcon sx={{ color: 'black' }} />}
                >
                    <Button onClick={() => handleFolder(null)}>
                        <Typography variant="h4" color="primary">
                            {tabUrlParam === 'home'
                                ? t('dashboards.home')
                                : t('dashboards.breadcrumbsRoot')}
                        </Typography>
                    </Button>
                    <Typography variant="h4" sx={{ color: 'black' }}>
                        {currentFolder?.name}
                    </Typography>
                </Breadcrumbs>
            )}
            {content}
        </Box>
    );
};

export default memo(DashboardsGrid);
